// =============================================
// File: src/hooks/useExertionRecoverySummary.js
// Purpose: Collate simple memoised values + recovery metrics
// =============================================
import { useMemo } from "react";
import { formatDateLong, formatTime } from "../utils/time";
import {
  computeRecoveryMetricsChosen,
  secondsToMinSec,
  chooseRecoveryModel,
} from "../utils/exertion/recovery";

export function useExertionRecoverySummary({ date, data }) {
  const subtitle = useMemo(() => {
    const d = date instanceof Date ? date : new Date(date ?? Date.now());
    return `${formatDateLong(d)} · ${formatTime(d)}`;
  }, [date]);

  const rrBpm = useMemo(() => {
    const v = Number(data?.estimated_rr_bpm);
    return Number.isFinite(v) ? Math.round(v) : null;
  }, [data?.estimated_rr_bpm]);

  const chosen = useMemo(() => chooseRecoveryModel(data), [data]);

  const tau = useMemo(() => {
    if (chosen.kind !== "exp") return null;
    const v = Number(chosen?.params?.tau);
    return Number.isFinite(v) ? Math.round(v) : null;
  }, [chosen]);

  const r2 = useMemo(() => {
    const v = Number(data?.decay_fit?.exp?.r2);
    return Number.isFinite(v) ? Math.round(v * 100) / 100 : null;
  }, [data?.decay_fit?.exp?.r2]);

  const durationSec = useMemo(() => {
    const v = Number(data?.duration_s_full);
    return Number.isFinite(v) ? Math.round(v) : null;
  }, [data?.duration_s_full]);

  const recovery = useMemo(() => computeRecoveryMetricsChosen(data), [data]);

  const breathingRecovery60 =
    recovery.ok && isFinite(recovery?.rrRecovery?.w60?.drop)
      ? Math.round(recovery.rrRecovery.w60.drop * 100) / 100
      : null;

  const breathingRecovery120 =
    recovery.ok && isFinite(recovery?.rrRecovery?.w120?.drop)
      ? Math.round(recovery.rrRecovery.w120.drop * 100) / 100
      : null;

  const recoveryTime90 = recovery.ok
    ? secondsToMinSec(recovery.timeToBaseline.seconds90).label
    : "–";

  const recoveryTime95 = recovery.ok
    ? secondsToMinSec(recovery.timeToBaseline.seconds95).label
    : "–";

  return {
    subtitle,
    rrBpm,
    tau,
    r2,
    durationSec,
    recovery,
    breathingRecovery60,
    breathingRecovery120,
    recoveryTime90,
    recoveryTime95,
  };
}
